import { Color, Spacing } from '@persona-id/design-tokens';
import { Suspense, useMemo, VFC } from 'react';
import styled from 'styled-components';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import ReactSelect from 'dashboard/components/ReactSelect';
import DraggableDivider from 'dashboard/components/WorkflowControlPanel/DraggableDivider';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import CodeEditor from 'dashboard/components-lib/CodeEditor/CodeEditor';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { formatHandlebarValue } from 'dashboard/lib/utils/handlebars';
import {
  generateJsonSchemaConfig,
  UsableObjectConfig,
  valueTransformer,
} from 'dashboard/lib/workflows';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionSendEmailV1 } from 'dashboard/models/workflow-version';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { JsonSchemaStringifiableTypes } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchemaTypes } from 'lib/json-schema';

const Clickable = styled.div`
  cursor: pointer;
`;

const StyledCodeHighlight = styled(CodeHighlight)<{ hasError: boolean }>`
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.color.variants.danger.default : theme.color.border.input};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const StyledSticker = styled(Sticker)`
  position: absolute;
  top: -${Spacing[3]};
  left: ${Spacing[4]};
  border: 1px solid ${Color.CorePurple200};
`;

const EditorContainer = styled.div`
  height: 100%;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

export const defaultParamsSendEmail = {
  recipient: '',
  subject: '',
  'from-email-address': '',
  'body-html': [
    '<style>',
    '\tbody {',
    '\t\tfont-family: sans-serif;',
    '\t\tbackground-color: #F1EBFF60;',
    '\t}',
    '',
    '\t.message {',
    '\t\tcolor: #AA85FF;',
    '\t}',
    '</style>',
    '',
    '<h3>This is a preview of your email</h3>',
    '<p class="message">Insert custom HTML code to be previewed.</p>',
  ].join('\n'),
  'body-text': '',
  'smtp-address': '',
  'smtp-port': '',
  'smtp-tls': true,
  'smtp-user-name': '',
  'smtp-password': '',
  'smtp-domain': '',
};

interface ParamsSendEmailV1Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionSendEmailV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionSendEmailV1['params']) => void;
}

const ParamsSendEmailV1: VFC<ParamsSendEmailV1Props> = ({
  step,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}) => {
  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects: usableObjects,
    });
  }, [usableObjects]);

  const vt = valueTransformer(tokensToDisplayValue);

  const bodyHtmlDisplay = useMemo(
    () => formatHandlebarValue(params['body-html'], vt.getDisplayValue),
    [params, vt],
  );

  const updateSendEmailParams = <Key extends keyof WorkflowActionSendEmailV1['params']>(
    key: Key,
    value: WorkflowActionSendEmailV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  const updateBodyHtml = (value: string) => {
    updateActionParams({
      ...params,
      'body-html': formatHandlebarValue(value, vt.getTokenizedValue),
    });
  };

  const [isOpenedHoverPanel, { enable: openHoverPanel, disable: closeHoverPanel }] =
    useToggle(false);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionSendEmailV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Recipient (Email address)' htmlFor='params-send-email__recipient' />
      <HandlebarFormControl
        size='md'
        type='input'
        value={params['recipient']}
        updateValue={(value: string) => {
          updateSendEmailParams('recipient', value);
        }}
        editable={editable}
        allowedTypes={[JsonSchemaTypes.String]}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-send-email__recipient'
        hasError={hasErrorFor('recipient')}
      />
      {renderErrorMessageFor('recipient')}
      <Spacer height={2} />
      <ParamLabel labelText='Subject' htmlFor='params-send-email__subject' />
      <HandlebarFormControl
        size='md'
        type='input'
        value={params['subject']}
        updateValue={(value: string) => {
          updateSendEmailParams('subject', value);
        }}
        editable={editable}
        allowedTypes={JsonSchemaStringifiableTypes}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-send-email__subject'
        hasError={hasErrorFor('subject')}
      />
      {renderErrorMessageFor('subject')}
      <Spacer height={2} />
      <Row>
        <ParamLabel
          htmlFor='params-send-email__body-html'
          labelText='HTML Body (Required)'
          helpText='Appearance may vary on different browsers and mail clients.'
        />
      </Row>
      <Suspense fallback={<p>Loading Previewer</p>}>
        <Clickable
          id='params-send-email__body-html'
          onClick={openHoverPanel}
          aria-label='Open an editor to edit email HTML body'
        >
          <StyledCodeHighlight language='handlebars' hasError={hasErrorFor('body-html')} light>
            {bodyHtmlDisplay.split('\n').slice(0, 15).join('\n')}
          </StyledCodeHighlight>
        </Clickable>
      </Suspense>
      {renderErrorMessageFor('body-html')}
      {isOpenedHoverPanel && (
        <MaximizablePanel
          title='Email Previewer'
          maximized
          onOutsideClickAndEscPress={closeHoverPanel}
        >
          {(fullScreen: boolean) => (
            <div style={{ height: fullScreen ? 'calc(100% - 48px)' : '450px' }}>
              <DraggableDivider
                orientation='horizontal'
                firstComponent={
                  <EditorContainer>
                    <CodeEditor
                      code={bodyHtmlDisplay}
                      language='html'
                      readOnly={!editable}
                      onChange={updateBodyHtml}
                      disableDebounce
                    />
                  </EditorContainer>
                }
                secondComponent={
                  <div style={{ height: '100%', position: 'relative' }}>
                    <StyledSticker variant='primary'>Preview</StyledSticker>
                    <iframe srcDoc={bodyHtmlDisplay} sandbox='' height='100%' width='100%' />
                  </div>
                }
                firstMinHeight='10px'
                secondMinHeight='10px'
                firstStyle={{ overflow: 'hidden' }}
              />
            </div>
          )}
        </MaximizablePanel>
      )}
      <Spacer height={2} />
      <Row>
        <ParamLabel
          htmlFor='params-send-email__body-text'
          labelText='Text Fallback (Required)'
          helpText='&lsquo;Text fallback&rsquo; is required as some Email clients do not support HTML.'
        />
      </Row>
      <HandlebarFormControl
        size='md'
        type='text-area'
        value={params['body-text']}
        updateValue={(value: string) => {
          updateSendEmailParams('body-text', value);
        }}
        editable={editable}
        allowedTypes={JsonSchemaStringifiableTypes}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-send-email__body-text'
        hasError={hasErrorFor('body-text')}
      />
      {renderErrorMessageFor('body-text')}
      <Spacer height={4} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <Spacer height={2} />
        <ParamLabel labelText='From email address' htmlFor='params-send-email__from-email' />
        <Input
          value={params['from-email-address']}
          onChange={(e) => updateSendEmailParams('from-email-address', e.target.value)}
          disabled={!editable}
          id='params-send-email__from-email'
          placeholder='no-reply@frompersona.com'
        />
        <Spacer height={2} />
        <ParamLabel labelText='SMTP server address' htmlFor='params-send-email__smtp-address' />
        <Input
          value={params['smtp-address']}
          onChange={(e) => updateSendEmailParams('smtp-address', e.target.value)}
          disabled={!editable}
          id='params-send-email__smtp-address'
        />
        <Spacer height={2} />
        <ParamLabel labelText='SMTP server port' htmlFor='params-send-email__smtp-port' />
        <Input
          value={params['smtp-port']}
          onChange={(e) => updateSendEmailParams('smtp-port', e.target.value)}
          disabled={!editable}
          id='params-send-email__smtp-port'
        />
        <Spacer height={2} />
        <ParamLabel labelText='SMTP server TLS' htmlFor='params-send-email__smtp-tls-input' />
        <ReactSelect
          value={String(params['smtp-tls'])}
          options={[
            {
              label: 'Enabled',
              value: 'true',
            },
            {
              label: 'Disabled',
              value: 'false',
            },
          ]}
          onChange={(val) => updateSendEmailParams('smtp-tls', val === 'true')}
          disabled={!editable}
          id='params-send-email__smtp-tls'
        />
        <Spacer height={2} />
        <ParamLabel labelText='SMTP server user' htmlFor='params-send-email__smtp-user' />
        <Input
          value={params['smtp-user-name']}
          onChange={(e) => updateSendEmailParams('smtp-user-name', e.target.value)}
          disabled={!editable}
          id='params-send-email__smtp-user'
        />
        <Spacer height={2} />
        <ParamLabel labelText='SMTP server password' htmlFor='params-send-email__smtp-password' />
        <Input
          value={params['smtp-password']}
          onChange={(e) => updateSendEmailParams('smtp-password', e.target.value)}
          type='password'
          disabled={!editable}
          id='params-send-email__smtp-password'
        />
        <Spacer height={2} />
        <ParamLabel labelText='Sender domain' htmlFor='params-send-email__smtp-domain' />
        <Input
          value={params['smtp-domain']}
          onChange={(e) => updateSendEmailParams('smtp-domain', e.target.value)}
          disabled={!editable}
          id='params-send-email__smtp-domain'
        />
        <Spacer height={2} />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsSendEmailV1;
